/**
 * the over all template for a collapsible element
 */
const template = document.createElement('template');
template.innerHTML = `
    <style>
       :host {
            display: block;
       }
       div.pyroce-title {
           display: flex;
           flex-direction: row;
           align-items: center;
       }
       span.pyroce-icon {
           padding-right: 4px;
           font-size: 0.8em;
           min-width: 1em;
           cursor: pointer;
       }
    </style>
	<div class="pyroce-title"><span class="pyroce-icon"><slot name="icon-collapsed"></slot><slot name="icon-expanded"></span></slot><slot name="title"></slot></div>
	<div><slot name="content"></slot></div>
`;

/**
 * template for default arrows using FontAwesome - works with FontAwesome 4 and above
 */
const defarrows = document.createElement('template');
defarrows.innerHTML = `
    <span slot="icon-collapsed"><i class="fa fa-angle-right"></i></span>
    <span slot="icon-expanded"><i class="fa fa-angle-down"></i></span>
`;

const CUSTOM_TAG = 'pyro-collapse';
const ATTR_KEY = 'key';
const ATTR_DEFAULTS = 'defaults';
const ATTR_COLLAPSED = 'collapsed';
const KEY_PREFIX = '_pyro-ce-key-';

/**
 * the implementation of the collapsible element
 */
export default class PyroCollapseElement extends HTMLElement {

    /**
     * constructs a new instance
     */
	constructor() {
		super();
        // Create a shadow root
        this.attachShadow({mode: 'open'});
		// set inner content
		this.shadowRoot.appendChild(template.content.cloneNode(true));
        // initialization flag
        this._initDone = false;
	}
	
    /**
     * called after the element was connected to the DOM
     */
    connectedCallback() {
		console.debug('<pyro-collapse> element connected.');
        if ( !this._initDone ) {
            this._initDone = true;
            this._initialize();
        } else {
            console.debug('Element already initialized. Probably re-connected.');
        }
	}

    /**
     * called if the value of an observed attribute was changed
     * @param {String} name attribute name
     * @param {String} oldValue previous value
     * @param {String} newValue new value
     */
	attributeChangedCallback(name, oldValue, newValue) {
        if ( this._contentDiv ) {
            console.debug(`Value of attribute "${name}" changed from "${oldValue}" to "${newValue}".`);
            switch ( name ) {
                case ATTR_KEY:
                    console.warn(`Cannot change the attribute "${name}"!`);
                    break;
                case ATTR_COLLAPSED:
                    this._renderState(newValue === 'true');
                    break;
            }
        }
    }

    /**
     * returns the list of observed attributes
     * @returns {String[]} the list of observerd attributes
     */
	static get observedAttributes() { 
        return [ ATTR_KEY, ATTR_COLLAPSED ]; 
    }

	/**
	 * @returns {String} the custom tag name
	 */
     static get customTag() {
		return CUSTOM_TAG;
	}

    _initialize() {
        console.debug('Initializing <pyro-collapse> element.');
		// get child div's
		const children = this.shadowRoot.querySelectorAll('div');;
		if ( children.length === 2 ) {
			this._titleDiv = children[0];
			this._contentDiv = children[1];
		} else {
			throw new Error('Invalid widget template!');
		}
        if ( 'true' === this.getAttribute(ATTR_DEFAULTS) ) {
            // we must clone the child elements of the template separately here
            const elms = defarrows.content.children;
            for ( let i=elms.length-1 ; i >= 0 ; --i ) {
                this.insertAdjacentElement('afterbegin', elms[i].cloneNode(true));
            }
        }
        this._onCollapseClicked = this._onCollapseClicked.bind(this);
        const span = this.shadowRoot.querySelector('span[class=pyroce-icon]');
        span.addEventListener('click', this._onCollapseClicked);
        // get icon elements
        this._iconCollapsed = this.shadowRoot.querySelector('slot[name=icon-collapsed]');
        this._iconExpanded = this.shadowRoot.querySelector('slot[name=icon-expanded]');
        // get initial attributes
        const key = this.getAttribute(ATTR_KEY) || '';
        Object.defineProperty(this, '_key', { value: KEY_PREFIX + key, enumerable: true, configurable: false, writable: false} );
        let collapsed = 'true' === this.getAttribute(ATTR_COLLAPSED);
        console.debug(`My storage key name is "${this._key}" and my initial collapsed state is "${collapsed}".`);
        if ( window.localStorage ) {
            // local storage is available - look there
            const stored = window.localStorage.getItem(this._key);
            if ( stored !== null && stored.length > 0 ) {
                console.debug(`Found stored value="${stored}".`);
                collapsed = stored === 'true';
            }
        }
        this._renderState(collapsed);
    }

    /**
     * called if the user clicks on the "collapse" or "expand" icon
     */
    _onCollapseClicked() {
        this._renderState(!this._collapsed);
        this._storeState();
        // fire custom event
        const event = new CustomEvent('collapse', { detail: { collapsed: this._collapsed } } );
        this.dispatchEvent(event);
    }

    /**
     * renders the element according to the new collapsed state
     * @param {Boolean} collapsed new collapsed state
     */
    _renderState(collapsed) {
        this._collapsed = collapsed;
        this._iconCollapsed.style.display = collapsed ? '' : 'none';
        this._iconExpanded.style.display = collapsed ? 'none' : '';
        this._contentDiv.style.display = collapsed ? 'none' : '';
    }

    /**
     * stores the current state
     */
    _storeState() {
        if ( window.localStorage && (this._key) && (this._key.length > 0) ) {
            window.localStorage.setItem(this._key, '' + this._collapsed);
        }
    }
}
